import { purchaseOrderItems } from "@/api/purchaseOrderItems/usePurchaseOrderItems";
import { customFunctions } from "@/config/customFunction";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormMain } from "@/features/standard/Form/FormMain";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";
import { ItemAutoComplete } from "@/features/ui/autoComplete/item/item-autoComplete";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import timeUtil from "@/utils/timeUtil";
import styled from "@emotion/styled";
import { Button, Checkbox, Flex, Select, Table, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  AuthSignupPost201ResponseEquipmentPurchaseOrderItem,
  ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner,
  PurchaseOrdersGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useQueries } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePruchaseOrderOutgingLogic } from "../hook/usePurchaseOrderOutging";

interface PurchaseOrderItemsSelectFormProps {
  formatterProps: PurchaseOrdersGet200ResponseRowsInner;
}

const Create = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Date: DateInput,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  ItemAutoComplete: ItemAutoComplete,
});

export const PurchaseOrderItemsOutgoingForm = (params: PurchaseOrderItemsSelectFormProps) => {
  const { formatterProps } = params;
  const { closeModal } = useModal();
  const [selectedRadio, setSelectedRadio] = useState<number[]>([]);
  const [selectedLotIds, setSelectedLotIds] = useState<{ [key: number]: string | null }>({}); // 각 행에 대한 선택된 LotId 상태
  const [selectLots, setSelectLots] = useState<{ [key: number]: any }>({}); // 각 행에 대한 selectLot 상태
  const { t } = useTranslation();

  const purchaseItemQueries = useQueries({
    queries: selectedRadio?.map((id) => {
      const { queryKey, queryFn } = purchaseOrderItems.detail({ purchaseOrderItemId: id });
      return {
        queryKey,
        queryFn,
        enabled: !!selectedRadio,
      };
    }),
  });

  const purchaseItemData = purchaseItemQueries
    ?.map(
      (query) =>
        (query.data as AxiosResponse<AuthSignupPost201ResponseEquipmentPurchaseOrderItem>)?.data,
    )
    .filter(Boolean);

  const { onSubmit, form, inventoryData } = usePruchaseOrderOutgingLogic(
    purchaseItemData,
    selectLots,
  );

  const handleRadioChange = (id: number) => {
    // 체크박스가 선택될 때마다 배열에 추가 또는 제거
    setSelectedRadio((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((item) => item !== id); // 이미 선택된 아이템이라면 제거
      } else {
        return [...prevSelected, id]; // 선택되지 않았다면 배열에 추가
      }
    });
  };

  const handleLotChange = (
    id: number | undefined,
    lotId: string,
    items: ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner,
  ) => {
    if (!id) return; // id가 없는 경우 예외 처리 추가
    setSelectedLotIds((prev) => ({ ...prev, [id]: lotId }));

    const matchedItem = inventoryData
      ?.flatMap((inventory) => inventory?.data)
      ?.find((item) => item?.lotId === Number(lotId) && item.itemCode === items.itemCode); // ✅ items 사용
    setSelectLots((prev) => ({ ...prev, [id]: matchedItem }));
  };

  useEffect(() => {
    // selectedRadio가 변경될 때 Select 값 초기화
    setSelectedLotIds({});
    setSelectLots({});
  }, [selectedRadio]);

  useEffect(() => {
    selectedRadio.forEach((id) => {
      if (selectLots[id]?.locationCode) {
        form.setFieldValue(`locationCode_${id}`, selectLots[id]?.locationCode || "");
      }
    });
  }, [selectLots, selectedRadio]);

  return (
    <>
      <Wrapper>
        <Table withBorder withColumnBorders w={"60rem"}>
          <thead>
            <Tr>
              <Th flex={0.5} className="small-column"></Th>
              <Th flex={2}>{t("item.name")}</Th>
              <Th flex={1}>{t("purchaseOrder.targetQuantity")}</Th>
              <Th flex={2}>{t("inventory.fromLocation")}</Th>
              <Th flex={2}>{"로트정보"}</Th>
              <Th flex={2}>{t("purchaseOrder.deliveryQuantity")}</Th>
              {customFunctions.ADD_PURCHASE_ORDER_INVOICE_DATE && (
                <Th flex={2}>{t("purchaseOrder.invoiceDate")}</Th>
              )}
            </Tr>
          </thead>
          {formatterProps.purchaseOrderItems &&
            formatterProps.purchaseOrderItems.length > 0 &&
            formatterProps.purchaseOrderItems.map((items, index) => (
              <tbody key={items.id}>
                <Tr>
                  <Td flex={0.5} className="small-column">
                    <Flex align="center" justify="center">
                      <Checkbox
                        value={items.id}
                        checked={!!selectedRadio.includes(items.id ?? 0)} // 배열에 포함되어 있으면 선택된 것으로 간주
                        onChange={() => handleRadioChange(items.id ?? 0)} // 선택된 상태를 추가/제거
                        disabled={Number(items.inputQuantity) >= Number(items.quantity)}
                      />
                    </Flex>
                  </Td>
                  <Td flex={2}>{items.itemCode}</Td>
                  <Td flex={1} className="textRight">
                    {items.quantity}
                  </Td>
                  <Td flex={2}>
                    <LocationAutoComplete
                      dropdownPosition="bottom"
                      withAsterisk
                      withinPortal
                      {...form.getInputProps(`locationCodes.${items.id}`)} // items.id를 key로 설정
                      defaultValue={
                        formatterProps?.sourceLocationCode ??
                        selectLots[items.id ?? 0]?.locationCode
                      }
                    />
                  </Td>
                  <Td flex={2}>
                    <Select
                      clearable
                      dropdownPosition="bottom"
                      value={items.id !== undefined ? selectedLotIds[items.id] : undefined} // 각 행의 id를 기준으로 상태 연결
                      data={
                        inventoryData
                          ?.flatMap((inventory) => inventory?.data)
                          ?.filter(
                            (invenItem) =>
                              invenItem !== undefined &&
                              invenItem.itemCode === items.itemCode && // 각 행의 itemCode 일치 조건 추가
                              typeof invenItem.quantity === "string" &&
                              Number(invenItem.quantity) > 0 &&
                              invenItem.locationCode === items.sourceLocationCode,
                          )
                          ?.map((item) => ({
                            value: item?.lotId ? item.lotId.toString() : "",
                            label: `${t("lot.name")}: ${item?.lot?.name ?? "-"} ${t("lot.expiration")}: ${item?.lot?.expiration ? timeUtil(item.lot.expiration) : "-"} ${t("location.code")}: ${item?.locationCode} ${t("inventory.quantity")}: ${item?.quantity}`,
                          })) || []
                      }
                      onChange={(e) => handleLotChange(items.id, e ?? "", items)} // items.id를 명확하게 전달
                      nothingFound={t("common.notFound")}
                    />
                  </Td>
                  <Td flex={2} className="textRight">
                    <Create.Input
                      withAsterisk
                      type="number"
                      {...form.getInputProps(`quantities.${items.id}`)} // items.id를 key로 설정
                      defaultValue={selectLots[items.id ?? 0]?.quantity || ""}
                      onChange={(e) => {
                        const val = Number(e.currentTarget.value);
                        const max = Number(
                          (formatterProps?.purchaseOrderItems &&
                            formatterProps?.purchaseOrderItems[index]?.quantity) ??
                            Infinity,
                        );
                        const result = val > max ? max : val;
                        form.setFieldValue(`quantities.${items.id}`, result.toString());
                      }}
                    />
                  </Td>
                  {customFunctions.ADD_PURCHASE_ORDER_INVOICE_DATE && (
                    <Td flex={2}>
                      <DateInput
                        label={t("purchaseOrder.invoiceDate")}
                        valueFormat="YYYY-MM-DD"
                        value={dayjs(form.values.invoiceDate).toDate()}
                        onChange={(e) => {
                          form.setFieldValue("invoiceDate", dayjs(e).startOf("day")?.toISOString());
                        }}
                      />
                    </Td>
                  )}
                </Tr>
              </tbody>
            ))}
        </Table>
        {formatterProps.purchaseOrderItems && formatterProps.purchaseOrderItems.length === 0 ? (
          <Flex>
            <Text size="xl" fw="bold">
              {t("purchaseOrderItems.none")}
            </Text>
          </Flex>
        ) : (
          <>
            <Create.ButtonBox>
              <Create.Button color="gray" onClick={closeModal}>
                {t("common.cancel")}
              </Create.Button>
              <Create.Button onClick={onSubmit} disabled={!selectedRadio}>
                {t("delivery.delivery")}
              </Create.Button>
            </Create.ButtonBox>
          </>
        )}
      </Wrapper>
    </>
  );
};

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const Th = styled.th<{ flex: number }>`
  padding: 0.5rem;
  text-align: center;
  flex: ${(props) => (props.flex ? props.flex : 1)};
`;

const Tr = styled.tr`
  display: flex;
  flex-direction: row;
`;
const Td = styled.td<{ flex: number }>`
  padding: 0.5rem;
  text-align: center;
  height: 5rem;
  flex: ${(props) => (props.flex ? props.flex : 1)};
  align-content: center;

  &.textRight {
    text-align: right;
  }
`;
